import type { Translations } from "../language";
import { counted } from "../plural";

/**
 * The words of the front page: `index.html` (each element marked `data-phrase`), `src/boot.ts` and
 * `src/front/` (the lobby's cards, the feedback forms, the sign-in chip, the language pennants).
 */
const en = {
  language: "Language",
  todaysMapLabel: "Today's map",
  todaysMap: (seedText: string) => `Today's map, ${seedText}`,
  namedMap: (seedText: string) => `Map "${seedText}"`,
  /** D-108: the map Play now plays with the lobby server, the company table's. */
  companyMapLabel: "Company map",
  companyMap: (seedText: string) => `Company map, ${seedText}`,
  /** FP1: the line over the title, the genre and the price of a try. */
  eyebrow: "Real-time hex strategy",
  /** FP1: the hook under the title. */
  headline: "Raise a kingdom. Settle the grudge.",
  pitch:
    "Your cities raise soldiers. You send them to war. Take the land, hold the cities, win in eight minutes.",
  /** VR1: what a try costs, one sentence under the pitch (FP1's four marks before it). */
  facts: "Free in your browser, no download. Solo or with friends, a new map every day.",
  /** FP1: the battle the front page plays behind its words, both kingdoms in their scripts' hands. */
  liveBattle: "Live battle",
  liveCaption: "Bots play both kingdoms here. Press Play now and Orange is yours.",
  playThisMap: "Play this map",
  kingdoms: { player: "Orange", rival: "Crimson" },
  wins: { player: "Orange wins!", rival: "Crimson wins!" },
  yourTurn: "Your turn.",
  cities: (count: number) => counted("en", count, { one: "city", other: "cities" }),
  land: "land",
  /** FP1: the day's map changes at midnight UTC (`seedFor`). */
  newMapIn: (hours: number, minutes: number) => `New map in ${hours} h ${minutes} min`,
  howTitle: "How it plays",
  stepSend: "Send",
  stepSendText: "Drag from your city to a hex: its soldiers march there.",
  stepGrow: "Grow",
  stepGrowText: "Cities train soldiers on their own. Found more with your King.",
  stepWin: "Win",
  stepWinText: "Take all the enemy's land, or hold more when the clock runs out.",
  orOnline: "Or join a table online",
  yourName: "Your name",
  newName: "New name",
  rollName: "Roll another name",
  emptySeats: "Bots fill the empty places.",
  playNow: "Play now",
  playNote: "Against a bot kingdom. Then pick your role: King or Soldier.",
  /** D-108: Play now's note with the lobby server: the online company; D-203: in the role picked. */
  playNoteOnline: "Online: five against five, as King or Soldier. Bots fill the empty places.",
  privacy: "What this playtest records",
  startFailed: "The game did not start. Reload the page. If it happens again, try another browser.",
  itch: "Also on itch.io",
  /** D-134: the game is free; the foot's link to its Patreon page. */
  patreon: "Support the game on Patreon",
  loading: "Loading…",
  /** FP1: the wait between a seat at a table and its start, over the battle and on Play now. */
  startsIn: "Your game starts in",
  takingSeat: "Finding your place…",
  startingIn: (time: string) => `Starting in ${time}…`,
  lobby: {
    duels: "Duels",
    duelsSoon: "Duels about to start",
    startsWhenYouJoin: "Starts when you join",
    seated: (humans: number, seats: number) => `${humans} / ${seats} players`,
    starting: "Starting…",
    script: "bot",
    scripts: (count: number) => counted("en", count, { one: "bot", other: "bots" }),
    ready: (versus: string, ready: number, humans: number) =>
      `${versus} · ${ready} of ${humans} ready`,
  },
  cards: {
    /** VR2: the title over each mode's tables, its sides' sizes after it. */
    modes: {
      duel: "Duel, 1 vs 1",
      company: "Company, 5 vs 5",
      allies: "Allies, 2 vs 2",
      war: "War, 10 vs 10",
    },
    mapOf: {
      duel: (id: string) => `Map of duel ${id}`,
      company: (id: string) => `Map of company ${id}`,
      allies: (id: string) => `Map of allies ${id}`,
      war: (id: string) => `Map of war ${id}`,
    },
    join: "Join",
    king: "King",
    soldier: "Soldier",
    startNow: "Start now",
    asked: "Ready",
    startNowAsKing:
      "Join as King if you have no place yet. The game starts when everyone here is ready. Bots fill the empty places.",
    startNowAtTable: "Start when everyone here is ready. Bots fill the empty places.",
    partyWord: "Party word",
    partyHint: "Friends who type the same word play in one kingdom",
    seatedButton: "Joined",
    refusals: {
      full: "The table is full",
      started: "Already started",
      unknown: "This duel is gone",
    },
    notSeated: "Could not join",
  },
  feedback: {
    label: "Feedback",
    question: "How was that match?",
    why: "Why did you win or lose? (optional)",
    ratings: { good: "Good", ok: "OK", bad: "Bad" },
    thanks: "Thanks!",
    skip: "Skip",
    bug: "Bug",
    reportBug: "Report a bug",
    whatWentWrong: "What went wrong?",
    whatYouSaw: "What you did, what you saw",
    send: "Send",
    cancel: "Cancel",
    sent: "Sent. Thank you!",
  },
  signIn: {
    failed: "Sign-in did not work. You are playing as a guest.",
    signOut: "Sign out",
    signedIn: "Signed in with Google",
    code: (account: string) => `Your playtest code: ${account}`,
    signIn: "Sign in with Google",
  },
  /** NM2: the line under the name, where a signed-in account's name is kept for it. */
  names: {
    yours: "Yours: nobody else plays under this name",
    taken: (name: string) => `“${name}” belongs to another player`,
    replaced: (name: string) => `“${name}” now belongs to another player: here is a new name`,
    reserved: "Names like “Guest 123” are reserved for the rooms",
    signInAgain: "Sign in again to keep your name",
    unsaved: "The server did not answer: your name is not kept yet",
  },
};

export const FRONT_TEXT: Translations<typeof en> = {
  en,
  ru: {
    language: "Язык",
    todaysMapLabel: "Карта дня",
    companyMapLabel: "Карта дружины",
    companyMap: (seedText) => `Карта дружины, ${seedText}`,
    todaysMap: (seedText) => `Карта дня, ${seedText}`,
    namedMap: (seedText) => `Карта «${seedText}»`,
    eyebrow: "Стратегия в реальном времени",
    headline: "Построй королевство. Сведи счёты.",
    pitch:
      "Твои города растят солдат, ты ведёшь их в бой. Захвати землю, удержи города — победа за восемь минут.",
    facts: "Бесплатно, в браузере, без установки. Один или с друзьями, новая карта каждый день.",
    liveBattle: "Идёт бой",
    liveCaption: "Здесь за оба королевства играют боты. Нажми «Играть» — оранжевое станет твоим.",
    playThisMap: "Играть на этой карте",
    kingdoms: { player: "Оранжевые", rival: "Багровые" },
    wins: { player: "Оранжевые победили!", rival: "Багровые победили!" },
    yourTurn: "Твой ход.",
    cities: (count) =>
      counted("ru", count, { one: "город", few: "города", many: "городов", other: "города" }),
    land: "земля",
    newMapIn: (hours, minutes) => `Новая карта через ${hours} ч ${minutes} мин`,
    howTitle: "Как играть",
    stepSend: "Отправь",
    stepSendText: "Тяни от своего города к гексу: его солдаты пойдут туда.",
    stepGrow: "Расти",
    stepGrowText: "Города сами обучают солдат. Новые основывает твой Король.",
    stepWin: "Победи",
    stepWinText: "Забери всю землю врага или держи больше земли, когда выйдет время.",
    orOnline: "Или сядь за стол онлайн",
    yourName: "Твоё имя",
    newName: "Новое имя",
    rollName: "Выбросить другое имя",
    emptySeats: "За пустые места играют боты.",
    playNow: "Играть",
    playNote: "Против королевства-бота. Потом выбери роль: Король или Солдат.",
    playNoteOnline: "Онлайн: пять на пять, Королём или Солдатом. Пустые места займут боты.",
    privacy: "Что записывает этот плейтест",
    startFailed:
      "Игра не запустилась. Перезагрузи страницу, а если не поможет — попробуй другой браузер.",
    itch: "Также на itch.io",
    patreon: "Поддержи игру на Patreon",
    loading: "Загрузка…",
    startsIn: "Твоя партия начнётся через",
    takingSeat: "Садимся за стол…",
    startingIn: (time) => `Старт через ${time}…`,
    lobby: {
      duels: "Дуэли",
      duelsSoon: "Дуэли скоро начнутся",
      startsWhenYouJoin: "Начнётся, когда войдёшь",
      seated: (humans, seats) => `${humans} / ${seats} за столом`,
      starting: "Начинаем…",
      script: "бот",
      scripts: (count) =>
        counted("ru", count, { one: "бот", few: "бота", many: "ботов", other: "бота" }),
      ready: (versus, ready, humans) => `${versus} · готовы ${ready} из ${humans}`,
    },
    cards: {
      modes: {
        duel: "Дуэль, 1 на 1",
        company: "Дружина, 5 на 5",
        allies: "Союзники, 2 на 2",
        war: "Война, 10 на 10",
      },
      mapOf: {
        duel: (id) => `Карта дуэли ${id}`,
        company: (id) => `Карта дружины ${id}`,
        allies: (id) => `Карта союзников ${id}`,
        war: (id) => `Карта войны ${id}`,
      },
      join: "Войти",
      king: "Король",
      soldier: "Солдат",
      startNow: "Начать сейчас",
      asked: "Готов",
      startNowAsKing:
        "Займи место Короля, если ты ещё не за столом. Игра начнётся, когда все здесь будут готовы. Пустые места займут боты.",
      startNowAtTable: "Начать, когда все здесь будут готовы. Пустые места займут боты.",
      partyWord: "Слово друзей",
      partyHint: "Друзья, набравшие одно слово, играют в одном королевстве",
      seatedButton: "За столом",
      refusals: {
        full: "Стол заполнен",
        started: "Игра уже идёт",
        unknown: "Этой дуэли больше нет",
      },
      notSeated: "Не удалось войти",
    },
    feedback: {
      label: "Отзыв",
      question: "Как тебе матч?",
      why: "Почему победа или поражение? (необязательно)",
      ratings: { good: "Хорошо", ok: "Норм", bad: "Плохо" },
      thanks: "Спасибо!",
      skip: "Пропустить",
      bug: "Баг",
      reportBug: "Сообщить о баге",
      whatWentWrong: "Что пошло не так?",
      whatYouSaw: "Что ты делал и что увидел",
      send: "Отправить",
      cancel: "Отмена",
      sent: "Отправлено. Спасибо!",
    },
    signIn: {
      failed: "Войти не удалось. Ты играешь как гость.",
      signOut: "Выйти",
      signedIn: "Аккаунт Google подключён",
      code: (account) => `Твой код плейтеста: ${account}`,
      signIn: "Войти через Google",
    },
    names: {
      yours: "Твоё: под этим именем больше никто не играет",
      taken: (name) => `«${name}» принадлежит другому игроку`,
      replaced: (name) => `«${name}» теперь у другого игрока: вот тебе новое имя`,
      reserved: "Имена вида «Guest 123» зарезервированы для комнат",
      signInAgain: "Войди снова, чтобы сохранить имя",
      unsaved: "Сервер не ответил: имя пока не закреплено",
    },
  },
  uk: {
    language: "Мова",
    todaysMapLabel: "Мапа дня",
    companyMapLabel: "Мапа дружини",
    companyMap: (seedText) => `Мапа дружини, ${seedText}`,
    todaysMap: (seedText) => `Мапа дня, ${seedText}`,
    namedMap: (seedText) => `Мапа «${seedText}»`,
    eyebrow: "Стратегія в реальному часі",
    headline: "Збудуй королівство. Поквитайся.",
    pitch:
      "Твої міста ростять солдатів, ти ведеш їх у бій. Захопи землю, втримай міста — перемога за вісім хвилин.",
    facts: "Безкоштовно, у браузері, без встановлення. Сам або з друзями, нова мапа щодня.",
    liveBattle: "Іде бій",
    liveCaption:
      "Тут за обидва королівства грають боти. Натисни «Грати» — помаранчеве стане твоїм.",
    playThisMap: "Грати на цій мапі",
    kingdoms: { player: "Помаранчеві", rival: "Багряні" },
    wins: { player: "Помаранчеві перемогли!", rival: "Багряні перемогли!" },
    yourTurn: "Твій хід.",
    cities: (count) =>
      counted("uk", count, { one: "місто", few: "міста", many: "міст", other: "міста" }),
    land: "земля",
    newMapIn: (hours, minutes) => `Нова мапа через ${hours} год ${minutes} хв`,
    howTitle: "Як грати",
    stepSend: "Надішли",
    stepSendText: "Тягни від свого міста до гекса: його солдати підуть туди.",
    stepGrow: "Рости",
    stepGrowText: "Міста самі навчають солдатів. Нові засновує твій Король.",
    stepWin: "Переможи",
    stepWinText: "Забери всю землю ворога або тримай більше землі, коли вийде час.",
    orOnline: "Або сядь за стіл онлайн",
    yourName: "Твоє ім'я",
    newName: "Нове ім'я",
    rollName: "Кинути інше ім'я",
    emptySeats: "За порожні місця грають боти.",
    playNow: "Грати",
    playNote: "Проти королівства-бота. Потім обери роль: Король чи Солдат.",
    playNoteOnline: "Онлайн: п'ять на п'ять, Королем чи Солдатом. Порожні місця займуть боти.",
    privacy: "Що записує цей плейтест",
    startFailed:
      "Гра не запустилася. Перезавантаж сторінку, а якщо не допоможе — спробуй інший браузер.",
    itch: "Також на itch.io",
    patreon: "Підтримай гру на Patreon",
    loading: "Завантаження…",
    startsIn: "Твоя партія почнеться через",
    takingSeat: "Сідаємо за стіл…",
    startingIn: (time) => `Старт через ${time}…`,
    lobby: {
      duels: "Дуелі",
      duelsSoon: "Дуелі скоро почнуться",
      startsWhenYouJoin: "Почнеться, коли увійдеш",
      seated: (humans, seats) => `${humans} / ${seats} за столом`,
      starting: "Починаємо…",
      script: "бот",
      scripts: (count) =>
        counted("uk", count, { one: "бот", few: "боти", many: "ботів", other: "бота" }),
      ready: (versus, ready, humans) => `${versus} · готові ${ready} з ${humans}`,
    },
    cards: {
      modes: {
        duel: "Дуель, 1 на 1",
        company: "Дружина, 5 на 5",
        allies: "Союзники, 2 на 2",
        war: "Війна, 10 на 10",
      },
      mapOf: {
        duel: (id) => `Мапа дуелі ${id}`,
        company: (id) => `Мапа дружини ${id}`,
        allies: (id) => `Мапа союзників ${id}`,
        war: (id) => `Мапа війни ${id}`,
      },
      join: "Увійти",
      king: "Король",
      soldier: "Солдат",
      startNow: "Почати зараз",
      asked: "Готовий",
      startNowAsKing:
        "Посядь місце Короля, якщо ти ще не за столом. Гра почнеться, коли всі тут будуть готові. Порожні місця займуть боти.",
      startNowAtTable: "Почати, коли всі тут будуть готові. Порожні місця займуть боти.",
      partyWord: "Слово друзів",
      partyHint: "Друзі, що набрали одне слово, грають в одному королівстві",
      seatedButton: "За столом",
      refusals: {
        full: "Стіл заповнений",
        started: "Гра вже йде",
        unknown: "Цієї дуелі вже немає",
      },
      notSeated: "Не вдалося увійти",
    },
    feedback: {
      label: "Відгук",
      question: "Як тобі матч?",
      why: "Чому перемога чи поразка? (необов'язково)",
      ratings: { good: "Добре", ok: "Норм", bad: "Погано" },
      thanks: "Дякуємо!",
      skip: "Пропустити",
      bug: "Баг",
      reportBug: "Повідомити про баг",
      whatWentWrong: "Що пішло не так?",
      whatYouSaw: "Що ти робив і що побачив",
      send: "Надіслати",
      cancel: "Скасувати",
      sent: "Надіслано. Дякуємо!",
    },
    signIn: {
      failed: "Увійти не вдалося. Ти граєш як гість.",
      signOut: "Вийти",
      signedIn: "Акаунт Google під'єднано",
      code: (account) => `Твій код плейтесту: ${account}`,
      signIn: "Увійти через Google",
    },
    names: {
      yours: "Твоє: під цим ім'ям більше ніхто не грає",
      taken: (name) => `«${name}» належить іншому гравцеві`,
      replaced: (name) => `«${name}» тепер в іншого гравця: ось тобі нове ім'я`,
      reserved: "Імена на кшталт «Guest 123» зарезервовано для кімнат",
      signInAgain: "Увійди знову, щоб зберегти ім'я",
      unsaved: "Сервер не відповів: ім'я ще не закріплено",
    },
  },
  es: {
    language: "Idioma",
    todaysMapLabel: "Mapa del día",
    todaysMap: (seedText) => `Mapa del día, ${seedText}`,
    namedMap: (seedText) => `Mapa «${seedText}»`,
    companyMapLabel: "Mapa de la compañía",
    companyMap: (seedText) => `Mapa de la compañía, ${seedText}`,
    eyebrow: "Estrategia en tiempo real",
    headline: "Alza un reino. Ajusta cuentas.",
    pitch:
      "Tus ciudades entrenan soldados y tú los mandas a la guerra. Toma la tierra, conserva las ciudades y gana en ocho minutos.",
    facts: "Gratis en tu navegador, sin descargas. Solo o con amigos, un mapa nuevo cada día.",
    liveBattle: "Batalla en directo",
    liveCaption: "Aquí los bots juegan los dos reinos. Pulsa «Jugar ya» y el naranja es tuyo.",
    playThisMap: "Jugar en este mapa",
    kingdoms: { player: "Naranja", rival: "Carmesí" },
    wins: { player: "¡Gana Naranja!", rival: "¡Gana Carmesí!" },
    yourTurn: "Te toca.",
    cities: (count) => counted("es", count, { one: "ciudad", other: "ciudades" }),
    land: "tierra",
    newMapIn: (hours, minutes) => `Mapa nuevo en ${hours} h ${minutes} min`,
    howTitle: "Cómo se juega",
    stepSend: "Envía",
    stepSendText: "Arrastra desde tu ciudad a una casilla: sus soldados marchan allí.",
    stepGrow: "Crece",
    stepGrowText: "Las ciudades entrenan soldados solas. Tu Rey funda más.",
    stepWin: "Gana",
    stepWinText: "Toma toda la tierra enemiga, o ten más cuando se acabe el tiempo.",
    orOnline: "O siéntate a una mesa en línea",
    yourName: "Tu nombre",
    newName: "Otro nombre",
    rollName: "Sortear otro nombre",
    emptySeats: "Los bots ocupan los sitios vacíos.",
    playNow: "Jugar ya",
    playNote: "Contra un reino bot. Luego elige tu rol: Rey o Soldado.",
    playNoteOnline:
      "En línea: cinco contra cinco, como Rey o Soldado. Los bots ocupan los sitios vacíos.",
    privacy: "Qué registra esta prueba de juego",
    startFailed:
      "El juego no se inició. Recarga la página y, si vuelve a pasar, prueba otro navegador.",
    itch: "También en itch.io",
    patreon: "Apoya el juego en Patreon",
    loading: "Cargando…",
    startsIn: "Tu partida empieza en",
    takingSeat: "Buscando sitio…",
    startingIn: (time) => `Empieza en ${time}…`,
    lobby: {
      duels: "Duelos",
      duelsSoon: "Duelos a punto de empezar",
      startsWhenYouJoin: "Empieza cuando entres",
      seated: (humans, seats) => `${humans} / ${seats} en la mesa`,
      starting: "Empezando…",
      script: "bot",
      scripts: (count) => counted("es", count, { one: "bot", other: "bots" }),
      ready: (versus, ready, humans) => `${versus} · ${ready} de ${humans} listos`,
    },
    cards: {
      modes: {
        duel: "Duelo, 1 contra 1",
        company: "Compañía, 5 contra 5",
        allies: "Aliados, 2 contra 2",
        war: "Guerra, 10 contra 10",
      },
      mapOf: {
        duel: (id) => `Mapa del duelo ${id}`,
        company: (id) => `Mapa de la compañía ${id}`,
        allies: (id) => `Mapa de aliados ${id}`,
        war: (id) => `Mapa de guerra ${id}`,
      },
      join: "Unirse",
      king: "Rey",
      soldier: "Soldado",
      startNow: "Empezar ya",
      asked: "Listo",
      startNowAsKing:
        "Entra como Rey si aún no tienes sitio. La partida empieza cuando todos en esta mesa estén listos. Los bots ocupan los sitios vacíos.",
      startNowAtTable:
        "Empieza cuando todos en esta mesa estén listos. Los bots ocupan los sitios vacíos.",
      partyWord: "Palabra del grupo",
      partyHint: "Los amigos que escriban la misma palabra juegan en el mismo reino",
      seatedButton: "En la mesa",
      refusals: {
        full: "La mesa está llena",
        started: "Ya ha empezado",
        unknown: "Este duelo ya no existe",
      },
      notSeated: "No se pudo entrar",
    },
    feedback: {
      label: "Opinión",
      question: "¿Qué tal la partida?",
      why: "¿Por qué ganaste o perdiste? (opcional)",
      ratings: { good: "Bien", ok: "Regular", bad: "Mal" },
      thanks: "¡Gracias!",
      skip: "Saltar",
      bug: "Error",
      reportBug: "Informar de un error",
      whatWentWrong: "¿Qué falló?",
      whatYouSaw: "Qué hiciste y qué viste",
      send: "Enviar",
      cancel: "Cancelar",
      sent: "Enviado. ¡Gracias!",
    },
    signIn: {
      failed: "No se pudo iniciar sesión. Juegas como invitado.",
      signOut: "Cerrar sesión",
      signedIn: "Sesión iniciada con Google",
      code: (account) => `Tu código de prueba: ${account}`,
      signIn: "Iniciar sesión con Google",
    },
    names: {
      yours: "Tuyo: nadie más juega con este nombre",
      taken: (name) => `«${name}» es de otro jugador`,
      replaced: (name) => `«${name}» ya es de otro jugador: aquí tienes otro nombre`,
      reserved: "Los nombres como «Guest 123» son para las salas",
      signInAgain: "Vuelve a iniciar sesión para conservar tu nombre",
      unsaved: "El servidor no respondió: tu nombre aún no está guardado",
    },
  },
};
